import { Box, Button, Grid, Typography, Theme } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import { EventAvailable, VisibilityOutlined, EventBusy, FlightTakeoff, FlightLand } from '@material-ui/icons';
import React, { useEffect, useState } from 'react';
import { TabletButton } from '@campfire/tablet-button';
import { unpackToDate } from '@campfire/hot-date';
import { DateTime } from 'luxon';
import { useVolunteerCommonProfileContext } from '../../VolunteerCommonProfileContext';
import { GET_VOLUNTEER_UNAVAILABILITIES } from '../../../../../global/components/AvailabilityCenter/get-volunteer-unavailabilities.gql';
import {
  AvailabilityCenterDialog,
  EnrolmentDataType,
  ValueProps,
} from '../../../../../common/availability-center/AvailabilityCenterDialog';
import {
  ProgramActivities,
  ProgramActivitiesVariables,
} from '../../../../../common/models/__generated__/ProgramActivities';
import { GET_ACTIVITYIDS } from '../../../../../common/models/program-activities.gql';
import {
  GetVolunteerEnrolmentsData,
  GetVolunteerEnrolmentsDataVariables,
} from '../../../../../common/models/volunteer-enrolments/__generated__/GetVolunteerEnrolmentsData';
import { GET_VOLUNTEER_ENROLMENTS_DATA } from '../../../../../common/models/volunteer-enrolments/volunteer-enrolments.gql';
import {
  AvailabilityCenterGetActivities,
  AvailabilityCenterGetActivitiesVariables,
} from '../../../../../global/components/AvailabilityCenter/__generated__/AvailabilityCenterGetActivities';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import { useSnackbar } from '../../../../../global/config/useSnackbar';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import { useCampfireLazyQuery } from '../../../../../global/network/useCampfireLazyQuery';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { VolunteerType } from '../../../../../common/models/fragments/__generated__/VolunteerType';
import { AlertFullSessionDialog } from '../../../../../common/alert-full-session-dialog/AlertFullSessionDialog';
import { GET_ACTIVITIES } from '../../../../../global/components/AvailabilityCenter/get-activities.gql';
import { SimpleAvailabilityComponent } from '../../../../../common/simple-availability-select/SimpleAvailabilitySelect';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';

interface Props {
  volunteer: VolunteerType;
}

const useLeavePeriodStyles = makeStyles((theme: Theme) =>
  createStyles({
    card: {
      backgroundColor: theme.alert.yellow.extraLight,
      borderRadius: '8px',
      padding: '10px 16px',
      marginBottom: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      border: `1px solid ${theme.alert.yellow.light}`,
    },
    cardHistorical: {
      backgroundColor: '#f5f5f5',
      borderRadius: '8px',
      padding: '10px 16px',
      marginBottom: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      border: '1px solid #e7e7e7',
    },
    content: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
    },
    icon: {
      color: theme.alert.yellow.medium,
    },
    iconHistorical: {
      color: '#999',
    },
    text: {
      fontSize: '14px',
      fontWeight: 500,
      color: theme.alert.yellow.dark,
      lineHeight: '1.2',
    },
    textHistorical: {
      fontSize: '14px',
      fontWeight: 500,
      color: '#666',
      lineHeight: '1.2',
    },
    dateText: {
      fontSize: '13px',
      color: theme.alert.yellow.medium,
      marginTop: '1px',
      lineHeight: '1.2',
    },
    dateTextHistorical: {
      fontSize: '13px',
      color: '#999',
      marginTop: '1px',
      lineHeight: '1.2',
    },
  })
);

const LeavePeriodsList = ({ leavePeriods }: { leavePeriods: any[] }) => {
  const { theme } = useCampfireTheme();
  const classes = useLeavePeriodStyles();
  const [showHistorical, setShowHistorical] = useState(false);
  const today = DateTime.local().startOf('day');

  const currentPeriods = leavePeriods.filter((lp) => unpackToDate(lp.endDate) >= today);
  const historicalPeriods = leavePeriods.filter((lp) => unpackToDate(lp.endDate) < today);

  const displayPeriods = showHistorical ? historicalPeriods : currentPeriods;

  return (
    <Box>
      <Box display='flex' justifyContent='space-between' alignItems='center' style={{ marginTop: '24px' }}>
        <Typography style={{ fontWeight: 600, fontSize: '16px', display: 'flex', alignItems: 'center', color: '#333' }}>
          <EventBusy style={{ fontSize: '14px', paddingRight: '5px' }} /> Leave Periods
        </Typography>
        <Button
          variant='outlined'
          startIcon={<VisibilityOutlined />}
          style={{
            height: 36,
            borderRadius: 6,
            border: '1px solid #DCDCDC',
            color: theme?.color?.grey?.neutral400,
            textTransform: 'capitalize',
          }}
          onClick={() => setShowHistorical(!showHistorical)}
        >
          <Typography
            style={{
              fontWeight: 600,
              fontSize: '13px',
              lineHeight: '19px',
              whiteSpace: 'nowrap',
            }}
          >
            {`Viewing: ${showHistorical ? 'Historical' : 'Current'}`}
          </Typography>
        </Button>
      </Box>
      <hr style={{ borderTop: '.5px solid #e7e7e7', margin: '12px 0' }} />
      {displayPeriods.length === 0 ? (
        <Typography variant='body2' style={{ color: '#888', padding: '8px 0' }}>
          {showHistorical ? 'No historical leave periods' : 'No upcoming or current leave periods'}
        </Typography>
      ) : (
        displayPeriods.map((lp: any) => {
          const start = unpackToDate(lp.startDate);
          const end = unpackToDate(lp.endDate);
          const isActive = start <= today && end >= today;
          const isCurrent = !showHistorical;
          const Icon = isActive ? FlightTakeoff : isCurrent ? FlightTakeoff : FlightLand;

          return (
            <Box key={lp.volunteerUnavailabilityId} className={isCurrent ? classes.card : classes.cardHistorical}>
              <Box className={classes.content}>
                <Icon className={isCurrent ? classes.icon : classes.iconHistorical} />
                <Box>
                  <Typography className={isCurrent ? classes.text : classes.textHistorical}>
                    {start.toFormat('dd MMM yyyy')} — {end.toFormat('dd MMM yyyy')}
                  </Typography>
                  <Typography className={isCurrent ? classes.dateText : classes.dateTextHistorical}>
                    {isActive ? 'Currently on leave' : isCurrent ? 'Upcoming leave' : 'Past leave'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          );
        })
      )}
    </Box>
  );
};

export const VolunteerAvailabilitySection = (props: Props) => {
  const {
    volunteerNameOptions: { preferredName },
  } = useVolunteerCommonProfileContext();
  const { volunteer } = props;

  const { data: unavailabilityData, refetch: refetchUnavailabilities } = useCampfireQuery(
    GET_VOLUNTEER_UNAVAILABILITIES,
    {
      options: { variables: { volunteerId: volunteer.volunteerId } },
    }
  );
  const leavePeriods = unavailabilityData?.vm.volunteer?.unavailabilities ?? [];

  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [value, setValue] = React.useState<ValueProps>({});
  const [fullSessionIds, setAllSessionIds] = React.useState<string[]>([]);
  const [activityDatesSubmited, setActivityDatesSubmited] = React.useState<string[]>([]);
  const [activityIdSubmited, setActivityIdSubmited] = React.useState<string>('');
  const [showAlertDialog, setShowAlertDialog] = React.useState(false);

  const [getEnrolmentData, { data: vmVolunteerData, refetch: refetchVolunteerData }] = useCampfireLazyQuery<
    GetVolunteerEnrolmentsData,
    GetVolunteerEnrolmentsDataVariables
  >(GET_VOLUNTEER_ENROLMENTS_DATA, {
    options: {
      variables: {
        volunteerId: volunteer.volunteerId,
      },
    },
  });

  const { data: vmPrograms } = useCampfireQuery<ProgramActivities, ProgramActivitiesVariables>(GET_ACTIVITYIDS, {
    options: {
      variables: {
        volunteerId: volunteer.volunteerId,
      },
    },
  });

  const activityIds =
    vmPrograms?.vm.volunteer?.programs.flatMap((program) =>
      program.activities.map((activity) => activity.activityId)
    ) || [];

  const [getActivities, { data: vmActivities }] = useCampfireLazyQuery<
    AvailabilityCenterGetActivities,
    AvailabilityCenterGetActivitiesVariables
  >(GET_ACTIVITIES, {
    options: {
      variables: {
        activityIds,
        from: DateTime.local().toISODate(),
        until: DateTime.local()
          .plus({ years: 1 })
          .toISODate(),
      },
    },
  });

  useEffect(() => {
    if (dialogOpen) {
      getEnrolmentData({
        variables: {
          volunteerId: volunteer.volunteerId,
        },
      });
      getActivities({
        variables: {
          activityIds,
          from: DateTime.local().toISODate(),
          until: DateTime.local()
            .plus({ years: 1 })
            .toISODate(),
        },
      });
    }
  }, [dialogOpen, volunteer]);

  const onChange = (newValue: ValueProps) => {
    setValue(newValue);
  };

  const campfireMutation = useCampfireFetch({ defer: true });
  const apiUrl = useApiUrl();
  const { setSnackbar } = useSnackbar();

  const handleSubmit = (selectedValue: ValueProps) => {
    if (selectedValue.operation === 'unavailability') {
      if (selectedValue.unavailabilities === undefined) return;
      campfireMutation
        .run({
          url: `${apiUrl}/vm/activity/enrolment/unavailability/bulk-save`,
          method: 'post',
          data: {
            volunteerId: volunteer.volunteerId,
            unavailabilities: selectedValue.unavailabilities,
          },
        })
        .then(() => {
          setSnackbar({
            variant: 'success',
            open: true,
            message: 'Leave periods updated successfully',
          });
          refetchUnavailabilities();
        });
      return;
    }

    const { activityId, sessionIds, dates } = selectedValue;
    if (!activityId || !dates?.length) {
      return;
    }
    const enrolment = vmVolunteerData?.vm.volunteer?.activityEnrolments.find(
      (activityEnrolment) => activityEnrolment.activity.activityId === activityId
    );

    campfireMutation
      .run({
        url: `${apiUrl}/vm/activity/enrolment/availability/save`,
        method: 'post',
        data: {
          activityEnrolmentId: enrolment?.activityEnrolmentId,
          activityDates: dates,
          activityRoleIds: [],
          sessionIds: sessionIds ?? [],
          activityId: activityId,
        },
      })
      .then((response) => {
        if (enrolment?.activity.hasOpenRoster) {
          campfireMutation.run({
            url: `${apiUrl}/vm/activity/enrolment/attendance/add`,
            method: 'post',
            data: {
              activityEnrolmentId: enrolment?.activityEnrolmentId,
              activityDates: dates,
              activityRoleIds: [],
              sessionIds: sessionIds ?? [],
              activityId: activityId,
              activityAvailabilityId: null,
              volunteerId: volunteer.volunteerId,
            },
          });
        }
        const data = response.data.data as {
          activityAvailabilityId: string[];
          activityId: string;
          fullSessionIds: string[];
          activityDates: string[];
        };

        if (data && data.fullSessionIds?.length > 0 && data.activityId) {
          setAllSessionIds(data.fullSessionIds);
          setActivityIdSubmited(data.activityId);
          setActivityDatesSubmited(data.activityDates);
          setShowAlertDialog(true);
        }

        setSnackbar({
          variant: 'success',
          open: true,
          message: 'Update availability successfully',
        });
        if (refetchVolunteerData) {
          setTimeout(refetchVolunteerData, 0);
        }
      });
  };

  React.useEffect(() => {
    setValue({});
  }, [dialogOpen]);

  const enrolmentDataByActivityId =
    vmVolunteerData?.vm.volunteer?.activityEnrolments.reduce(
      (acc, activityEnrolment) => ({
        ...acc,
        [activityEnrolment.activity.activityId]: {
          availabilities: activityEnrolment.availabilities
            .filter((availability) => !availability.dateRemoved)
            .map((availability) => availability.activityDate),
          unavailabilities: activityEnrolment.unavailabilities
            .filter((unavailability) => unavailability.dateRemoved)
            .map((unavailability) => unavailability.activityDate),
        },
      }),
      {} as EnrolmentDataType
    ) || {};

  const enrolmentActivities = vmActivities?.vm.activities.filter(
    (activity) => enrolmentDataByActivityId[activity.activityId as string]
  );

  const handleAlertClose = () => {
    setShowAlertDialog(false);
    setAllSessionIds([]);
    setActivityDatesSubmited([]);
    setActivityIdSubmited('');
  };

  return (
    <Grid container spacing={2} direction='column' style={{ position: 'relative' }}>
      <Grid item xs={12} style={{ textAlign: 'left' }}>
        <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
          <Box>
            <Typography
              style={{ fontWeight: 600, fontSize: '18px', display: 'flex', alignItems: 'center', color: '#333' }}
            >
              <EventAvailable style={{ fontSize: '14px', paddingRight: '5px' }} /> Availability
            </Typography>
            <Typography variant='body2' style={{ color: '#464646' }} align='left'>
              {preferredName}&apos;s activity availability
            </Typography>
          </Box>
          <TabletButton
            size='small'
            variant='contained'
            color='primary'
            aria-controls='options-menu'
            aria-haspopup='true'
            onClick={() => setDialogOpen(true)}
            startIcon={<EventAvailable />}
            style={{
              height: '100%',
              border: '1px solid transparent',
              boxSizing: 'border-box',
              borderRadius: '4px',
              padding: '6px 12px',
              marginRight: '10px',
              color: '#fff',
            }}
          >
            Availability Centre
          </TabletButton>
        </Box>
        <hr style={{ borderTop: '.5px solid #e7e7e7', margin: '20px 0' }} />
        {volunteer.profile.profileId && (
          <SimpleAvailabilityComponent profileId={volunteer.profile.profileId} editing={false} />
        )}
        <LeavePeriodsList leavePeriods={leavePeriods} />
      </Grid>
      {dialogOpen && (
        <AvailabilityCenterDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          value={value}
          onChange={onChange}
          activities={enrolmentActivities || []}
          onSubmit={handleSubmit}
          enrolmentData={enrolmentDataByActivityId}
          managerView
        />
      )}
      {showAlertDialog && (
        <AlertFullSessionDialog
          open={showAlertDialog}
          onClose={handleAlertClose}
          fullSessionIds={fullSessionIds}
          activityIdSubmited={activityIdSubmited}
          activityDatesSubmited={activityDatesSubmited}
        />
      )}
    </Grid>
  );
};
